import { IconProps } from '@tamagui/helpers-icon';
import { ChevronDown } from '@tamagui/lucide-icons';
import {
  AccordionContext,
  AccordionItemContext,
  useAccordion,
  useAccordionItem
} from '@unistocks/components/accordion/accordion-context';
import { NativePressable, NativePressableProps } from '@unistocks/components/basic/pressable';
import { medium } from '@unistocks/shared/animations';
import { PRESSABLE_OPACITY } from '@unistocks/shared/consts';
import { arrayify, difference, isDefined, union } from '@unistocks/shared/utils/util';
import { MaybeArray } from '@unistocks/types';
import { ComponentType, PropsWithChildren, ReactNode, useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useControllableState, View, ViewProps, withStaticProperties } from 'tamagui';

// ─── Types ───────────────────────────────────────────────────────────────────

type SingleAccordionProps = {
  type?: 'single';
  collapsible?: boolean;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value?: string) => void;
};

type MultipleAccordionProps = {
  type: 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

type AccordionRootProps = (SingleAccordionProps | MultipleAccordionProps) & ViewProps;

type AccordionItemProps = ViewProps & {
  value: string;
};

type TriggerRenderFn = (state: { open: boolean }) => ReactNode;

type AccordionTriggerProps = Omit<NativePressableProps, 'children'> & {
  children: ReactNode | TriggerRenderFn;
};

type AccordionIconTriggerProps = Omit<AccordionTriggerProps, 'children' | 'style'> & {
  icon?: ComponentType<IconProps>;
  iconSize?: IconProps['size'];
  style?: StyleProp<ViewStyle>;
};

// ─── AccordionRoot ────────────────────────────────────────────────────────────

function AccordionRoot(props: AccordionRootProps) {
  const {
    type = 'single',
    children,
    value: valueProp,
    defaultValue,
    onValueChange: onValueChangeProp,
    ...viewProps
  } = props as AccordionRootProps & {
    collapsible?: boolean;
    value?: MaybeArray<string>;
    defaultValue?: MaybeArray<string>;
    onValueChange?: (v: MaybeArray<string>) => void;
  };

  const collapsible =
    type === 'single' ? ((props as SingleAccordionProps).collapsible ?? false) : true;

  const [value, onValueChange] = useControllableState<MaybeArray<string> | undefined>({
    strategy: 'most-recent-wins',
    defaultProp: defaultValue as MaybeArray<string> | undefined,
    prop: valueProp as MaybeArray<string> | undefined,
    onChange: next => {
      const nextValues = isDefined(next) ? arrayify(next) : [];

      if (type === 'single') {
        onValueChangeProp?.(nextValues[0] ?? undefined);
      } else {
        onValueChangeProp?.(nextValues);
      }
    }
  });

  const openValues = isDefined(value) ? arrayify(value) : [];

  const toggle = (itemValue: string) => {
    let newValues: string[];

    if (type === 'single') {
      if (openValues.includes(itemValue)) {
        if (!collapsible) return;
        newValues = [];
      } else {
        newValues = [itemValue];
      }
    } else {
      if (openValues.includes(itemValue)) {
        newValues = difference(openValues, itemValue);
      } else {
        newValues = union(openValues, itemValue);
      }
    }

    onValueChange(type === 'single' ? newValues[0] : newValues);
  };

  return (
    <AccordionContext value={{ type, collapsible, openValues, toggle }}>
      <View {...viewProps}>{children}</View>
    </AccordionContext>
  );
}

AccordionRoot.displayName = 'Accordion';

// ─── AccordionItem ────────────────────────────────────────────────────────────

function AccordionItem({ value, children, ...viewProps }: AccordionItemProps) {
  const { openValues } = useAccordion();
  const isOpen = openValues.includes(value);

  return (
    <AccordionItemContext value={{ value, isOpen }}>
      <View {...viewProps}>{children}</View>
    </AccordionItemContext>
  );
}

AccordionItem.displayName = 'Accordion.Item';

// ─── AccordionTrigger ─────────────────────────────────────────────────────────

function AccordionTriggerBase({ children, onPress, ...pressableProps }: AccordionTriggerProps) {
  const { value, isOpen } = useAccordionItem();
  const { toggle } = useAccordion();

  const content =
    typeof children === 'function' ? (children as TriggerRenderFn)({ open: isOpen }) : children;

  return (
    <NativePressable
      withFeedback
      hoverStyle={{ backgroundColor: 'transparent', borderColor: 'transparent' }}
      pressStyle={{ opacity: PRESSABLE_OPACITY, backgroundColor: 'transparent' }}
      onPress={e => {
        toggle(value);
        onPress?.(e);
      }}
      {...pressableProps}
    >
      {content}
    </NativePressable>
  );
}

AccordionTriggerBase.displayName = 'Accordion.Trigger';

// ─── AccordionHeightAnimator (internal) ───────────────────────────────────────

function AccordionHeightAnimator({
  children,
  onClosed
}: PropsWithChildren<{ onClosed?: () => void }>) {
  const { isOpen } = useAccordionItem();
  const contentHeight = useSharedValue(0);

  const derivedHeight = useDerivedValue(() =>
    withTiming(contentHeight.value * Number(isOpen), medium, finished => {
      if (finished && onClosed && !isOpen) {
        scheduleOnRN(onClosed);
      }
    })
  );

  const animatedStyle = useAnimatedStyle(() => ({
    height: derivedHeight.value
  }));

  return (
    <Animated.View style={[{ overflow: 'hidden' }, animatedStyle]}>
      <View
        style={{ position: 'absolute', top: 0, left: 0, right: 0 }}
        onLayout={e => {
          contentHeight.value = e.nativeEvent.layout.height;
        }}
      >
        {children}
      </View>
    </Animated.View>
  );
}

type AccordionContentProps = ViewProps & {
  forceMount?: boolean;
};

// ─── AccordionContent ─────────────────────────────────────────────────────────

function AccordionContent({ children, style, forceMount, ...viewProps }: AccordionContentProps) {
  const { isOpen } = useAccordionItem();
  const [render, setRender] = useState(isOpen);

  if (isOpen && !render) {
    setRender(true);
  }

  if (!render && !forceMount) return null;

  return (
    <AccordionHeightAnimator onClosed={forceMount ? undefined : () => setRender(false)}>
      <Animated.View
        style={[
          {
            opacity: isOpen ? 1 : 0,
            transform: [{ translateY: isOpen ? 0 : -20 }],
            transitionProperty: ['opacity', 'transform'],
            transitionDuration: medium.duration!,
            transitionTimingFunction: 'ease-out'
          },
          style as StyleProp<ViewStyle>
        ]}
        {...(viewProps as object)}
      >
        {children}
      </Animated.View>
    </AccordionHeightAnimator>
  );
}

AccordionContent.displayName = 'Accordion.Content';

// ─── AccordionIconTrigger ──────────────────────────────────────────────────

function AccordionIconTrigger({
  icon: Icon = ChevronDown,
  iconSize = '$2',
  style,
  ...triggerProps
}: AccordionIconTriggerProps) {
  const { isOpen } = useAccordionItem();

  return (
    <AccordionTriggerBase
      style={[{ alignItems: 'center', justifyContent: 'center' }, style]}
      {...triggerProps}
    >
      <Animated.View
        style={{
          transform: [{ rotate: isOpen ? '180deg' : '0deg' }],
          transitionProperty: 'transform',
          transitionDuration: medium.duration!,
          transitionTimingFunction: 'ease-out'
        }}
      >
        <Icon size={iconSize} />
      </Animated.View>
    </AccordionTriggerBase>
  );
}

AccordionIconTrigger.displayName = 'Accordion.IconTrigger';

// ─── Assembly ─────────────────────────────────────────────────────────────────

export const Accordion = withStaticProperties(AccordionRoot, {
  Item: AccordionItem,
  Trigger: AccordionTriggerBase,
  Content: AccordionContent,
  IconTrigger: AccordionIconTrigger
});

export { useAccordion, useAccordionItem };
export type {
  AccordionContentProps,
  AccordionIconTriggerProps,
  AccordionItemProps,
  AccordionRootProps as AccordionProps,
  AccordionTriggerProps
};
